/**
 * POST /api/v1/quotes/options, POST /api/v1/quotes, GET /api/v1/quotes/{id}.
 *
 * The client never computes a price. It asks the server what every preset costs
 * (`/quotes/options`), renders those integers, and when the customer commits it
 * asks the server to store one (`/quotes`) — sending *what* to print, never what
 * it should cost. That is why there is no amount in any request type here.
 */
import { api } from './client';
import { queryKeys, useApiQuery } from './queryClient';
import type {
  DocumentRequest,
  FulfilmentMethod,
  BindingPriceListOut,
  BindingPriceRequest,
  OptionPriceListOut,
  OptionPriceRequest,
  QuoteCreateRequest,
  QuoteOut,
} from './types';

export const QUOTES_PATH = '/api/v1/quotes';
export const QUOTE_OPTIONS_PATH = '/api/v1/quotes/options';
export const QUOTE_BINDINGS_PATH = '/api/v1/quotes/bindings';

export function fetchOptionPrices(
  request: OptionPriceRequest,
  signal?: AbortSignal,
): Promise<OptionPriceListOut> {
  return api.post<OptionPriceListOut>(QUOTE_OPTIONS_PATH, request, signal ? { signal } : undefined);
}

export interface UseOptionPricesArgs {
  uploadId: string | undefined;
  copies: number;
  /** Six digits once the customer has entered one, else null (shipping pends). */
  pincode?: string | null;
  /** Holds the binding fixed so each preset's total is comparable. */
  bindingSlug?: string | null;
  /**
   * The rest of the basket, held fixed. Each row then answers "what would the
   * ORDER cost with this preset?", which is what the screen is asking.
   */
  others?: DocumentRequest[];
  fulfilmentMethod?: FulfilmentMethod;
}

/**
 * Live per-preset totals.
 *
 * A POST used as a query, which looks odd until you notice the request body is
 * four scalars and the response is pure derivation with no side effect — the
 * backend models it as a calculation, not a resource. Keying the cache on those
 * scalars gives us the read semantics anyway: toggling copies 1 → 2 → 1 issues
 * two requests, not three.
 *
 * `staleTime: 0` on purpose. A price is the one thing that must not be served
 * from a stale cache when the catalogue may have moved underneath it.
 */
export function useOptionPrices({
  uploadId,
  copies,
  pincode = null,
  bindingSlug = null,
  others = [],
  fulfilmentMethod = 'delivery',
}: UseOptionPricesArgs) {
  const normalisedPincode = pincode && /^[1-9][0-9]{5}$/.test(pincode) ? pincode : null;
  const basketKey = basketCacheKey(others, fulfilmentMethod);

  return useApiQuery<OptionPriceListOut, ReturnType<typeof queryKeys.optionPrices>>({
    queryKey: queryKeys.optionPrices(
      uploadId ?? '',
      copies,
      normalisedPincode,
      bindingSlug,
      basketKey,
    ),
    queryFn: ({ signal }) =>
      fetchOptionPrices(
        {
          upload_id: uploadId ?? '',
          copies,
          ...(normalisedPincode ? { pincode: normalisedPincode } : {}),
          ...(bindingSlug ? { binding_slug: bindingSlug } : {}),
          ...(others.length ? { others } : {}),
          fulfilment_method: fulfilmentMethod,
        },
        signal,
      ),
    enabled: Boolean(uploadId),
    staleTime: 0,
    // Keep the previous prices on screen while the next set loads, so changing
    // copies dims the numbers instead of collapsing the whole list to skeletons.
    placeholderData: (previous) => previous,
  });
}

export function fetchBindingPrices(
  request: BindingPriceRequest,
  signal?: AbortSignal,
): Promise<BindingPriceListOut> {
  return api.post<BindingPriceListOut>(
    QUOTE_BINDINGS_PATH,
    request,
    signal ? { signal } : undefined,
  );
}

/**
 * A stable cache key for "the rest of the basket".
 *
 * Serialised rather than hashed: these lists are at most ten short objects, and
 * a readable key is worth more in the React Query devtools than the handful of
 * bytes a hash would save.
 */
function basketCacheKey(others: DocumentRequest[], method: FulfilmentMethod): string {
  return `${method}:${others
    .map((entry) =>
      [
        entry.upload_id,
        entry.print_option_id ?? entry.print_option_slug ?? '',
        entry.binding_option_id ?? entry.binding_slug ?? '',
        entry.copies ?? 1,
        entry.cover_colour ?? '',
      ].join('/'),
    )
    .join('|')}`;
}

export interface UseBindingPricesArgs {
  uploadId: string | undefined;
  /** Bindings are priced against ONE preset — see the note below. */
  printOptionSlug: string | undefined;
  copies: number;
  pincode?: string | null;
  /** The rest of the basket, held fixed. See `UseOptionPricesArgs`. */
  others?: DocumentRequest[];
  fulfilmentMethod?: FulfilmentMethod;
}

/**
 * Live per-binding totals for the binding picker.
 *
 * Priced against the currently selected preset, not against the document alone,
 * because the two choices interact: duplex halves the sheets in a finished book,
 * so a document too thick to spiral-bind single-sided fits comfortably
 * double-sided. One binding list shared across all presets would be wrong for at
 * least one of them.
 *
 * That makes this query and `useOptionPrices` mutually parameterised — each is
 * keyed on the other's current selection. It is not circular: both are pure
 * derivations of the same URL state, and each re-runs only when its own key
 * changes.
 */
export function useBindingPrices({
  uploadId,
  printOptionSlug,
  copies,
  pincode = null,
  others = [],
  fulfilmentMethod = 'delivery',
}: UseBindingPricesArgs) {
  const normalisedPincode = pincode && /^[1-9][0-9]{5}$/.test(pincode) ? pincode : null;
  const basketKey = basketCacheKey(others, fulfilmentMethod);

  return useApiQuery<BindingPriceListOut, ReturnType<typeof queryKeys.bindingPrices>>({
    queryKey: queryKeys.bindingPrices(
      uploadId ?? '',
      printOptionSlug ?? '',
      copies,
      normalisedPincode,
      basketKey,
    ),
    queryFn: ({ signal }) =>
      fetchBindingPrices(
        {
          upload_id: uploadId ?? '',
          print_option_slug: printOptionSlug ?? '',
          copies,
          ...(normalisedPincode ? { pincode: normalisedPincode } : {}),
          ...(others.length ? { others } : {}),
          fulfilment_method: fulfilmentMethod,
        },
        signal,
      ),
    enabled: Boolean(uploadId) && Boolean(printOptionSlug),
    staleTime: 0,
    placeholderData: (previous) => previous,
  });
}

export function createQuote(request: QuoteCreateRequest): Promise<QuoteOut> {
  return api.post<QuoteOut>(QUOTES_PATH, request);
}

export function fetchQuote(quoteId: string, signal?: AbortSignal): Promise<QuoteOut> {
  return api.get<QuoteOut>(`${QUOTES_PATH}/${encodeURIComponent(quoteId)}`, {
    ...(signal ? { signal } : {}),
  });
}

/**
 * The stored quote behind `?q=`.
 *
 * This is what makes the Options step recoverable: coming back to it from a
 * later step re-reads the customer's preset and copy count from the server
 * rather than from anything the component remembered.
 */
export function useQuote(quoteId: string | undefined) {
  return useApiQuery<QuoteOut, ReturnType<typeof queryKeys.quote>>({
    queryKey: queryKeys.quote(quoteId ?? ''),
    queryFn: ({ signal }) => fetchQuote(quoteId ?? '', signal),
    enabled: Boolean(quoteId),
    // A quote expires. Re-reading it on mount is how we notice.
    staleTime: 0,
  });
}

/**
 * The caller's open basket, or null.
 *
 * Null is a normal answer, not an error: most visitors most of the time have
 * no cart. The server decides ownership — the signed-in user, or the browser
 * session when anonymous — so this works before and after signing in without
 * the client tracking anything.
 */
export function fetchCurrentBasket(signal?: AbortSignal): Promise<QuoteOut | null> {
  return api.get<QuoteOut | null>(`${QUOTES_PATH}/current`, signal ? { signal } : undefined);
}

/**
 * Empty the basket.
 *
 * 204 whether or not there was one: the caller asked for an empty cart, and
 * that is true afterwards either way.
 */
export function discardBasket(): Promise<void> {
  return api.delete<void>(`${QUOTES_PATH}/current`);
}

export function useCurrentBasket() {
  return useApiQuery<QuoteOut | null, ReturnType<typeof queryKeys.currentBasket>>({
    queryKey: queryKeys.currentBasket(),
    queryFn: ({ signal }) => fetchCurrentBasket(signal),
    // The header shows this on every page, and a basket changes whenever the
    // customer edits it. Short rather than zero: re-reading on every mount of
    // every page would be a request per navigation for a number that is
    // usually unchanged.
    staleTime: 10_000,
  });
}
